import React, { useEffect, useState } from 'react';
import Button from '../../../../../components/Form/Button';
import { InfoModal } from '../../../../../components/Modal/InfoModal';
import { Icon, Icons } from 'components/Icon';
import TextAreaFieldWithAutoHeightResize from 'components/Form/TextAreaFieldWithAutoHeightResize';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useApproveRejectCustomerWill } from 'common/queries-and-mutations/admin/customer';
import { useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';

type Props = {
  onSuccess: () => void;
  onClose: () => void;
  className?: string;
  customerId: number;
};

export default function WillRejectModal({
  onSuccess,
  onClose,
  className = 'w-[20%]',
  customerId
}: Readonly<Props>) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAppSelector(selectAccountDetails);
  const { mutate, isSuccess, isError } = useApproveRejectCustomerWill();

  const formik = useFormik({
    initialValues: {
      note: ''
    },
    validationSchema: Yup.object({
      note: Yup.string()
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      handleDeactivate(values);
    }
  });

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  const handleDeactivate = ({ note }: { note: string }) => {
    if (!formik.values.note) {
      formik.setFieldError(
        'note',
        'Please enter reason why you want to reject this customer will'
      );
      return;
    }
    mutate({
      note,
      isApproved: false,
      customerId,
      staffId: +user.id!
    });
  };

  return (
    <InfoModal width={className} className="h-fit rounded-lg py-2">
      <div className="flex w-full flex-col items-center bg-white font-circular text-2xl font-bold text-[#353535]">
        <form onSubmit={formik.handleSubmit} className="w-full">
          <div className="flex items-center justify-between border-b border-[#E5E5E5] p-3 px-5">
            <p className="text-left text-base font-medium">
              Reject Customer Will
            </p>
            <div className="" role="button" onClick={onClose}>
              <Icon name={Icons.Close} />
            </div>
          </div>
          <div className="p-5">
            <div className="group relative z-0 mb-6 mt-3 w-full">
              <TextAreaFieldWithAutoHeightResize
                label="Note"
                name={`note`}
                value={formik.values.note}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder=""
                rows={8}
                error={formik.touched.note ? formik.errors.note : ''}
                className="w-full rounded-2xl border-[1.5px] border-gray-300"
              />
            </div>
          </div>
          <div className="mr-6 flex items-center justify-end gap-4 pb-4">
            <Button
              variant="outline"
              size="md"
              className="mb-3 w-[10rem] rounded px-[1.5rem] py-1 text-sm font-normal text-primary-800"
              label="Cancel"
              onClick={onClose}
            ></Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="mb-3 w-[10rem] rounded text-sm font-normal text-white"
              label="Reject"
              loading={isSubmitting}
              disabled={isSubmitting}
            ></Button>
          </div>
        </form>
      </div>
    </InfoModal>
  );
}
